import { Flex, Heading, HStack, Icon, IconButton, Table, Tbody, Th, Thead, Tooltip } from '@chakra-ui/react'
import { FC, Fragment, memo, useCallback, useRef, useState } from 'react'
import { MessageTimelineTableWrapper, TableScrollWrapper, TimelineTr } from './styled'
import { useHotkeys } from 'react-hotkeys-hook'
import { TimelineTableRow } from './TimelineTableRow'
import { DividerPlacement, SplitterValues, TableSplitterRow } from './TableSplitterRow'
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi'
import { TbSortAscendingNumbers, TbSortDescendingNumbers } from 'react-icons/tb'

const directionType = {
    ASCENDING: {
        up: 'reverse',
        down: 'normal',
        upFirst: 'normal',
        downLast: 'normal'
    },
    DESCENDING: {
        up: 'normal',
        down: 'reverse',
        upFirst: 'normal',
        downLast: 'normal'
    }
} as any

interface Props {
    data: any[]
    selected: number
    onRowSelect: (rowNum: number) => void
    onSortChange: (val: string) => void
    isSearch: boolean | undefined
    searchParams?: any
    handleDividerClick: (direction: SplitterValues, seqNum: number, deltaSeqNum: number) => void
}

export const MessageTimelineTable: FC<Props> = memo(({
    data,
    selected,
    onRowSelect,
    onSortChange,
    isSearch,
    handleDividerClick,
    searchParams
}) => {
    const [currentSort, setCurrentSort] = useState('DESCENDING')
    const [tableLayoutAuto, setTableLayoutAuto] = useState(false)

    // @ts-ignore
    const selectedRowRef = useRef<TimelineTableRow>()
    // @ts-ignore
    const tableRef = useRef<Table>()

    const handleSortChange = () => {
        const newSortValue = currentSort === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING'
        onSortChange(newSortValue)
        setCurrentSort(newSortValue)
    }

    const handleSelectChange = (direction: number) => {
        if (direction === -1 && selected > 0) {
            onRowSelect(isSearch ? data[selected - 1].seqNumber : selected - 1)
        } else if (direction === 1 && selected < data.length - 1) {
            onRowSelect(isSearch ? data[selected + 1].seqNumber : selected + 1)
        }
    }

    useHotkeys(
        'up',
        (e) => {
            e.preventDefault()
            handleSelectChange(-1)

            const rect = selectedRowRef?.current.getBoundingClientRect()
            const tableRect = tableRef?.current.getBoundingClientRect()
            if (rect.top < tableRect.top) {
                selectedRowRef?.current.scrollIntoView({ block: 'nearest', inline: 'start' })
                // @ts-ignore
                tableRef.current.scrollTop -= 30
            }
        },
        {},
        [selected]
    )
    useHotkeys(
        'down',
        (e) => {
            e.preventDefault()
            handleSelectChange(1)
            //@ts-ignore
            const rect = selectedRowRef?.current.getBoundingClientRect()
            const tableRect = tableRef?.current.getBoundingClientRect()
            if (rect.bottom > tableRect.bottom) {
                selectedRowRef?.current.scrollIntoView(false)
            }
        },
        {},
        [selected]
    )

    const onSplitterClick = useCallback(
        (direction: SplitterValues, itemNumber: number, nextItemNumber: number) => {
            const currentDirectionType: string = directionType[currentSort][direction]

            handleDividerClick(
                direction,
                currentDirectionType === 'normal' ? itemNumber : nextItemNumber,
                currentDirectionType === 'normal' ? nextItemNumber : itemNumber
            )
        },
        [currentSort, handleDividerClick]
    )

    const getSessionID = (item: any) => 
        item.rw === 'W'
            ? `${item.senderCompID}:${item.targetCompID}`
            : `${item.targetCompID}:${item.senderCompID}`

    
    // Map sessionID -> list of gaps
    // Used mostly for trouble shouting purposes
    const sessionGaps: any = {}
    // isPossibleSessionGap[index] === true, if after 'index' element might be a session gap
    const isPossibleSessionGap: boolean[] = []
    // Find sessions gaps (only if secondaryQuery is specified,
    // otherwise we only have to show first and last splitter)
    if (isSearch && searchParams.secondaryQuery) {
        if (!searchParams.filteredSessions.length) {
            for (let i = 0; i < data.length; i++) {
                isPossibleSessionGap[i] = true
                sessionGaps["NO FILTERED SESSIONS DEFINED, USE ALL GAPS"] = true
            }
        } else {
            const previousMessages: any = {}
            data.forEach((item: any, index: any) => {
                const sessionID = getSessionID(item)
                const previous = previousMessages[sessionID]

                if (!previous) {
                    // Mark "head" gap (from possible "newer" session message
                    // to first presented message)
                    sessionGaps[sessionID] = []
                    sessionGaps[sessionID].push({
                        start: Number.NEGATIVE_INFINITY,
                        end: index
                    })
                    if (index > 0) {
                        for (let i = 0; i < index; i++) {
                            isPossibleSessionGap[i] = true
                        }
                    }
                } else {
                    // Mark gaps between two messages from the same session
                    const previousItem = previous.item
                    const previousIndex = previous.index
                    
                    if (Math.abs(previousItem.seqNumber - item.seqNumber) > 1
                        && Math.abs(previousItem.rSeq - item.rSeq) > 1) {

                        if (!sessionGaps[sessionID]) {
                            sessionGaps[sessionID] = []
                        }
                        sessionGaps[sessionID].push({
                            start: previousIndex,
                            end: index
                        })
                        for (let i = previousIndex; i < index; i++) {
                            isPossibleSessionGap[i] = true
                        }
                    }
                } 

                previousMessages[sessionID] = { item, index }
            })

            const presentedSessionIDs = new Set(Object.keys(sessionGaps))
            
            // If any of filtered sessions is not presented in the search results,
            // mark all as possible gaps
            for (const filteredSession of searchParams.filteredSessions) {
                if (!presentedSessionIDs.has(filteredSession)) {
                    for (let i = 0; i < data.length; i++) {
                        isPossibleSessionGap[i] = true
                    }
                    sessionGaps[filteredSession] = []
                    sessionGaps[filteredSession].push({
                        start: Number.NEGATIVE_INFINITY,
                        end: Number.POSITIVE_INFINITY
                    })
                }
            }

            // Mark "tail" gap (from last presented session message,
            // to possible "older" message)
            for (let i = (data.length - 1); i >= 0; i--) {
                const item = data[i]
                const sessionID = getSessionID(item)
                isPossibleSessionGap[i] = true;

                if (presentedSessionIDs.has(sessionID)) {
                    sessionGaps[sessionID].push({
                        start: i,
                        end: Number.POSITIVE_INFINITY
                    })
                    presentedSessionIDs.delete(sessionID)
                }

                if (!presentedSessionIDs.size) {
                    break
                }
            }
        }
    }

    const renderDivider = (previousItem: any, item: any, nextItem: any, index: number, place: string) => {
        if (!isSearch) return null

        if (index === 0 && place === DividerPlacement.Before && !(currentSort === 'ASCENDING' && item.seqNumber === 0)) {
            return (
                <TableSplitterRow
                    key={item._noResultsBefore}
                    value={SplitterValues.Up}
                    emptyCellsAmount={6}
                    itemNumber={currentSort === 'ASCENDING' ? -1 : item.seqNumber + 1}
                    nextItemNumber={currentSort === 'ASCENDING' ? item.seqNumber - 1 : -1}
                    onSplitterClick={onSplitterClick}
                />
            )
        } else if (place === DividerPlacement.After && !nextItem && !(currentSort === 'DESCENDING' && item.seqNumber === 0)) {
            return (
                <TableSplitterRow
                    key={item._noResultsAfter}
                    value={SplitterValues.Down}
                    emptyCellsAmount={6}
                    itemNumber={currentSort === 'ASCENDING' ? item.seqNumber + 1 : -1}
                    nextItemNumber={currentSort === 'ASCENDING' ? -1 : item.seqNumber - 1}
                    onSplitterClick={onSplitterClick}
                />
            )
        } else if (place === DividerPlacement.After && nextItem && Math.abs(item.seqNumber - nextItem.seqNumber) > 1) {
            if (!item._noGapAfter && isPossibleSessionGap[index]) {
                return <TableSplitterRow
                    key={nextItem.seqNumber}
                    value={SplitterValues.UpDown}
                    emptyCellsAmount={6}
                    itemNumber={item.seqNumber}
                    nextItemNumber={nextItem.seqNumber}
                    onSplitterClick={onSplitterClick}
                />
            }
        } else {
            return null
        }
    }

    // console.log('isPossibleSessionGap', isPossibleSessionGap)
    // console.log('sessionGaps', sessionGaps)
    // console.log('[', selected, '][', data[selected]?.seqNumber, '][', data[selected]?.rSeq, '][', data[selected]?.wSeq, ']')

    return (
        <MessageTimelineTableWrapper>
            <HStack spacing={1}>
                <Heading sx={{ textAlign: 'left' }} as="h5" size="md">
                    Timeline
                </Heading>
                <Tooltip label={tableLayoutAuto ? 'Show all columns' : 'Adjust columns width to data'} openDelay={500}>
                    <IconButton
                        aria-label='Change table layout'
                        icon={tableLayoutAuto ? <FiMinimize2 style={{ transform: 'rotate(45deg)' }}/> : <FiMaximize2 style={{ transform: 'rotate(45deg)' }}/>}
                        onClick={() => setTableLayoutAuto(!tableLayoutAuto)}
                        variant='ghost'
                        size='xs'
                    />
                </Tooltip>
            </HStack> 
            <TableScrollWrapper ref={tableRef} style={{ maxHeight: '70vh' }}>
                <Table
                    size="sm"
                    colorScheme="white"
                    fontFamily='var(--code-font-family)'
                    sx={{
                        tableLayout: tableLayoutAuto ? 'auto' : 'fixed',
                        height: 'fit-content',
                        minWidth: '630px',
                        overflowY: 'auto',
                    }}
                >
                    <Thead>
                        <TimelineTr>
                            <Th style={{ width: tableLayoutAuto ? undefined : '105px'}}>
                                <Flex>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            background: 'var(--chakra-colors-background)',
                                            left: -24,
                                            // manually set height, to fix Safari issue
                                            height: 25,
                                            width: 24,
                                            top: 0
                                        }}
                                    />
                                    Time{' '}
                                    {isSearch && (
                                        <Tooltip label={currentSort}>
                                            <Flex>
                                                <Icon
                                                    style={{ cursor: 'pointer', height: '16px', width: '16px' }}
                                                    as={currentSort === 'ASCENDING' ? TbSortAscendingNumbers : TbSortDescendingNumbers}
                                                    onClick={handleSortChange}
                                                />
                                            </Flex>
                                        </Tooltip>
                                    )}
                                </Flex>
                            </Th>
                            {isSearch && <Th style={{ width: tableLayoutAuto ? undefined : '30px' }}>R/W</Th>}
                            <Th>Sender</Th>
                            <Th>Target</Th>
                            <Th style={{ width: tableLayoutAuto ? undefined : '150px' }}>Message Type</Th>
                            <Th>ClOrdId</Th>
                            <Th style={{ width: tableLayoutAuto ? undefined : '160px' }}>Detail</Th>
                        </TimelineTr>
                    </Thead>
                    <Tbody>
                        {data.map((item: any, index: number) => (
                            <Fragment key={item.seqNumber}>
                                {renderDivider(data[index - 1], item, data[index + 1], index, DividerPlacement.Before)}
                                <TimelineTableRow
                                    key={index}
                                    index={isSearch ? data[index].seqNumber : index}
                                    data={item}
                                    isAppeared={item._inserted}
                                    isSelected={index === selected}
                                    isSearch={isSearch}
                                    isEven={index % 2 === 0}
                                    isTruncate={!tableLayoutAuto}
                                    onRowSelect={onRowSelect}
                                    {...(index === selected ? { ref: selectedRowRef } : {})}
                                />
                                {renderDivider(data[index - 1], item, data[index + 1], index, DividerPlacement.After)}
                            </Fragment>
                        ))}
                    </Tbody>
                </Table>
            </TableScrollWrapper>
        </MessageTimelineTableWrapper>
    )
})
